import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    IconButton,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    alpha,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { 
    Edit, 
    Delete, 
    CheckCircle, 
    Cancel, 
    Add, 
    Close,
    Person,
    PersonAdd
} from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getUsers, addUser, editUser, deleteUser, getRoles } from '../../http/userApi';
import { createPortal } from 'react-dom';
import { useMap } from 'react-leaflet';

// Функции для генерации inline стилей
const getTableContainerSx = () => ({
    backgroundColor: 'var(--background-paper)',
    border: '1px solid var(--border-default)',
    borderRadius: { xs: '6px', md: '8px' },
    '& .MuiTable-root': {
        borderCollapse: 'collapse',
    },
});

const getTableHeadSx = () => ({
    '& .MuiTableCell-head': {
        backgroundColor: 'var(--table-header-bg)',
        color: 'var(--text-secondary)',
        fontWeight: 600,
        fontSize: { xs: '10px', md: '11px' },
        padding: { xs: '8px 10px', md: '10px 12px' },
        borderBottom: '1px solid var(--border-default)',
        borderRight: '1px solid var(--border-default)',
        whiteSpace: 'nowrap',
        position: 'sticky',
        top: 0,
        zIndex: 1,
        '&:last-child': {
            borderRight: 'none',
        },
    },
});

const getTableRowSx = () => ({
    transition: 'background-color 0.15s ease',
    backgroundColor: 'var(--table-row-bg)',
    '&:hover': {
        backgroundColor: 'var(--table-row-hover)',
    },
});

const getTableCellSx = () => ({
    fontSize: { xs: '10px', md: '11px' },
    padding: { xs: '6px 10px', md: '8px 12px' },
    color: 'var(--text-primary)',
    borderBottom: '1px solid var(--table-border)',
    borderRight: '1px solid var(--table-border)',
    '&:last-child': {
        borderRight: 'none',
    },
});

const getEditTableRowSx = (mode) => ({
    backgroundColor: mode === 'add' 
        ? 'var(--success-bg)' 
        : 'var(--warning-bg)',
    '& .MuiTableCell-root': {
        borderBottom: '1px solid var(--table-border)',
        borderRight: '1px solid var(--table-border)',
        '&:last-child': {
            borderRight: 'none',
        },
    },
});

const getActionButtonSx = (btncolor = 'primary') => ({
    padding: { xs: '3px', md: '4px' },
    borderRadius: { xs: '4px', md: '6px' },
    transition: 'all 0.15s ease',
    color: btncolor === 'error' ? 'var(--error-color)' : 
           btncolor === 'success' ? 'var(--success-color)' : 
           btncolor === 'warning' ? 'var(--warning-color)' :
           'var(--text-secondary)',
    '&:hover': {
        backgroundColor: btncolor === 'error' ? 'var(--error-bg)' : 
                        btncolor === 'success' ? 'var(--success-bg)' : 
                        btncolor === 'warning' ? 'var(--warning-bg)' :
                        'var(--hover-bg)',
    },
});

const ModalUsers = props => {
    const map = useMap();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isDarkMode = useSelector(state => state.settings.darkMode);
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [refresh, setRefresh] = useState(false);
    const [data, setData] = useState(null);
    const [addUserClicked, setAddUserClicked] = useState(null);
    const [editUserClicked, setEditUserClicked] = useState(null);

    const primaryColor = isDarkMode ? '#58a6ff' : '#0969da';
    const successColor = isDarkMode ? '#3fb950' : '#2da44e';

    const inputSx = {
        '& .MuiOutlinedInput-root': {
            backgroundColor: 'var(--background-paper)',
            borderRadius: { xs: '4px', md: '6px' },
            '& fieldset': { borderColor: 'var(--border-default)' },
            '&:hover fieldset': { borderColor: primaryColor },
            '&.Mui-focused fieldset': { borderColor: primaryColor }
        },
        '& .MuiInputBase-input': { 
            padding: { xs: '5px 8px', md: '6px 10px' }, 
            fontSize: { xs: '10px', md: '11px' },
            color: 'var(--text-primary)'
        },
        '& .MuiInputLabel-root': { color: 'var(--text-muted)' },
    };

    const selectSx = {
        backgroundColor: 'var(--background-paper)',
        borderRadius: { xs: '4px', md: '6px' },
        '& .MuiSelect-select': { 
            padding: { xs: '5px 8px', md: '6px 10px' }, 
            fontSize: { xs: '10px', md: '11px' },
            color: 'var(--text-primary)'
        },
        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--border-default)' },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: primaryColor },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: primaryColor },
        '& .MuiSelect-icon': { color: 'var(--text-secondary)' },
    };

    const menuPropsSx = {
        PaperProps: {
            sx: {
                backgroundColor: 'var(--background-paper)',
                border: '1px solid var(--border-default)',
                boxShadow: 'var(--shadow-lg)',
                borderRadius: { xs: '4px', md: '6px' },
                '& .MuiMenuItem-root': {
                    fontSize: { xs: '10px', md: '11px' },
                    color: 'var(--text-primary)',
                    minHeight: { xs: 32, md: 36 },
                    '&:hover': { backgroundColor: 'var(--hover-bg)' },
                    '&.Mui-selected': { 
                        backgroundColor: 'var(--selected-bg)',
                        '&:hover': { backgroundColor: 'var(--selected-bg)' }
                    },
                },
            },
        },
    };

    useEffect(() => {
        getUsers()
            .then((res) => setUsers(res.data))
            .catch((error) => console.error('Ошибка загрузки пользователей:', error));
        
        getRoles()
            .then((res) => setRoles(res.data))
            .catch((error) => console.error('Ошибка загрузки ролей:', error));
        
        setRefresh(false);
    }, [refresh]);

    function del(user) {
        if (!window.confirm(`Удалить пользователя "${user.login}"?`)) return;
        deleteUser(user)
            .then(() => {
                setRefresh(true);
                setAddUserClicked(null);
                setEditUserClicked(null);
            })
            .catch((error) => console.error('Ошибка удаления пользователя:', error));
    }

    function add(user) {
        addUser(user)
            .then(() => {
                setRefresh(true);
                setAddUserClicked(null);
            })
            .catch((error) => console.error('Ошибка создания пользователя:', error));
    }

    function edit(user) {
        editUser(user)
            .then(() => {
                setRefresh(true);
                setEditUserClicked(null);
            })
            .catch((error) => console.error('Ошибка обновления пользователя:', error));
    }

    return createPortal(
        <Dialog 
            open={true}
            maxWidth="md"
            fullWidth
            fullScreen={isMobile}
            onClose={() => map.fire('modalclose')}
            scroll="paper"
            PaperProps={{
                sx: {
                    backgroundColor: 'var(--background-default)',
                    color: 'var(--text-primary)',
                    backgroundImage: 'none',
                    borderRadius: { xs: '8px', md: '12px' },
                    border: '1px solid var(--border-default)',
                    boxShadow: 'var(--shadow-lg)',
                    margin: { xs: '8px', md: '32px' },
                    maxHeight: { xs: 'calc(100% - 16px)', md: 'calc(100% - 64px)' }
                }
            }}
        >
            <DialogTitle sx={{ 
                p: { xs: '8px 12px', md: '10px 16px' }, 
                backgroundColor: 'var(--background-default)',
                borderBottom: '1px solid var(--border-default)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: { xs: 'wrap', md: 'nowrap' },
                gap: { xs: 0.75, md: 0 }
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 1.5 }, flexWrap: 'wrap' }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: { xs: 24, md: 28 },
                        height: { xs: 24, md: 28 },
                        borderRadius: { xs: '4px', md: '6px' },
                        backgroundColor: primaryColor,
                        color: 'white'
                    }}>
                        <Person sx={{ fontSize: { xs: 14, md: 16 } }} />
                    </Box>
                    <Typography sx={{ fontSize: { xs: '0.85rem', md: '0.95rem' }, fontWeight: 600, color: 'var(--text-primary)' }}>
                        Управление пользователями
                    </Typography>
                    <Chip 
                        label={users.length}
                        size="small"
                        sx={{
                            height: { xs: 18, md: 20 },
                            fontSize: { xs: '9px', md: '10px' },
                            fontWeight: 600,
                            backgroundColor: alpha(primaryColor, 0.12),
                            color: primaryColor
                        }}
                    />
                </Box>
                <IconButton 
                    size="small" 
                    onClick={() => map.fire('modalclose')}
                    sx={{ 
                        color: 'var(--text-secondary)',
                        padding: { xs: '4px', md: '8px' }
                    }}
                >
                    <Close sx={{ fontSize: { xs: 18, md: 20 } }} />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: { xs: 1, md: 2 }, backgroundColor: 'var(--background-default)' }}>
                <TableContainer component={Paper} elevation={0} sx={getTableContainerSx()}>
                    <Table size="small" stickyHeader>
                        <TableHead sx={getTableHeadSx()}>
                            <TableRow>
                                <TableCell>Логин</TableCell>
                                <TableCell>Имя</TableCell>
                                <TableCell>Роль</TableCell>
                                <TableCell>Комментарий</TableCell>
                                <TableCell align="center" sx={{ width: 80 }}>Действия</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.map((user) => (
                                <TableRow key={user.id} sx={getTableRowSx()}>
                                    <TableCell sx={{ ...getTableCellSx(), fontWeight: 500 }}>{user.login}</TableCell>
                                    <TableCell sx={getTableCellSx()}>{user.name}</TableCell>
                                    <TableCell sx={getTableCellSx()}>
                                        {user.roles.map((role, index) => (
                                            <Chip 
                                                key={index}
                                                label={role} 
                                                size="small" 
                                                sx={{ 
                                                    mr: { xs: 0.4, md: 0.5 }, 
                                                    mb: { xs: 0.25, md: 0 },
                                                    height: { xs: 16, md: 18 },
                                                    fontSize: { xs: '9px', md: '10px' },
                                                    fontWeight: 600,
                                                    backgroundColor: alpha(primaryColor, 0.12),
                                                    color: primaryColor,
                                                    border: 'none'
                                                }}
                                            />
                                        ))}
                                    </TableCell>
                                    <TableCell sx={{ ...getTableCellSx(), color: 'var(--text-muted)' }}>
                                        {user.comments || '—'}
                                    </TableCell>
                                    <TableCell align="center" sx={getTableCellSx()}>
                                        <Box sx={{ display: 'flex', gap: { xs: 0.4, md: 0.5 }, justifyContent: 'center' }}>
                                            <IconButton 
                                                size="small"
                                                onClick={() => {
                                                    setData(user);
                                                    setEditUserClicked(user);
                                                    setAddUserClicked(null);
                                                }}
                                                sx={getActionButtonSx('warning')}
                                            >
                                                <Edit sx={{ fontSize: { xs: 14, md: 16 } }} />
                                            </IconButton>
                                            <IconButton 
                                                size="small"
                                                onClick={() => del(user)}
                                                sx={getActionButtonSx('error')}
                                            >
                                                <Delete sx={{ fontSize: { xs: 14, md: 16 } }} />
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                            
                            {/* Строка добавления */}
                            {addUserClicked && (
                                <TableRow sx={getEditTableRowSx('add')}>
                                    <TableCell sx={getTableCellSx()}>
                                        <TextField 
                                            size="small" 
                                            placeholder="Логин" 
                                            fullWidth
                                            sx={inputSx}
                                            onChange={(e) => setData({ ...data, login: e.target.value })} 
                                        />
                                    </TableCell>
                                    <TableCell sx={getTableCellSx()}>
                                        <TextField 
                                            size="small" 
                                            placeholder="Имя" 
                                            fullWidth
                                            sx={inputSx}
                                            onChange={(e) => setData({ ...data, name: e.target.value })} 
                                        />
                                    </TableCell>
                                    <TableCell sx={getTableCellSx()}>
                                        <FormControl size="small" fullWidth>
                                            <Select
                                                value={data?.roles?.[0] || ''}
                                                onChange={(e) => setData({ ...data, roles: [e.target.value] })}
                                                displayEmpty
                                                sx={selectSx}
                                                MenuProps={menuPropsSx}
                                            >
                                                <MenuItem value="" disabled>
                                                    <em style={{ color: 'var(--text-muted)' }}>Роль</em>
                                                </MenuItem>
                                                {roles.map((role) => (
                                                    <MenuItem key={role.name} value={role.name}>{role.name}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell sx={getTableCellSx()}>
                                        <TextField 
                                            size="small" 
                                            placeholder="Комментарий" 
                                            fullWidth
                                            sx={inputSx}
                                            onChange={(e) => setData({ ...data, comments: e.target.value })} 
                                        />
                                    </TableCell>
                                    <TableCell align="center" sx={getTableCellSx()}>
                                        <Box sx={{ display: 'flex', gap: { xs: 0.4, md: 0.5 }, justifyContent: 'center' }}>
                                            <IconButton 
                                                size="small"
                                                onClick={() => add(data)}
                                                sx={getActionButtonSx('success')}
                                            >
                                                <CheckCircle sx={{ fontSize: { xs: 14, md: 16 } }} />
                                            </IconButton>
                                            <IconButton 
                                                size="small"
                                                onClick={() => {
                                                    setData(null);
                                                    setAddUserClicked(null);
                                                }}
                                                sx={getActionButtonSx('error')}
                                            >
                                                <Cancel sx={{ fontSize: { xs: 14, md: 16 } }} />
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )}

                            {/* Строка редактирования */}
                            {editUserClicked && (
                                <TableRow sx={getEditTableRowSx('edit')}>
                                    <TableCell sx={getTableCellSx()}>
                                        <Typography sx={{ 
                                            fontSize: { xs: '10px', md: '11px' }, 
                                            fontWeight: 600, 
                                            color: 'var(--text-primary)',
                                            py: { xs: 0.4, md: 0.5 }
                                        }}>
                                            {data.login}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={getTableCellSx()}>
                                        <TextField 
                                            size="small" 
                                            placeholder="Имя" 
                                            fullWidth
                                            defaultValue={data.name}
                                            sx={inputSx}
                                            onChange={(e) => setData({ ...data, name: e.target.value })} 
                                        />
                                    </TableCell>
                                    <TableCell sx={getTableCellSx()}>
                                        <FormControl size="small" fullWidth>
                                            <Select
                                                value={data.roles[0]} 
                                                onChange={(e) => setData({ ...data, roles: [e.target.value] })}
                                                sx={selectSx}
                                                MenuProps={menuPropsSx}
                                            >
                                                {roles.map((role) => (
                                                    <MenuItem key={role.name} value={role.name}>{role.name}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell sx={getTableCellSx()}>
                                        <TextField 
                                            size="small" 
                                            placeholder="Комментарий" 
                                            fullWidth
                                            defaultValue={data.comments}
                                            sx={inputSx}
                                            onChange={(e) => setData({ ...data, comments: e.target.value })} 
                                        />
                                    </TableCell>
                                    <TableCell align="center" sx={getTableCellSx()}>
                                        <Box sx={{ display: 'flex', gap: { xs: 0.4, md: 0.5 }, justifyContent: 'center' }}>
                                            <IconButton 
                                                size="small"
                                                onClick={() => edit(data)}
                                                sx={getActionButtonSx('success')}
                                            >
                                                <CheckCircle sx={{ fontSize: { xs: 14, md: 16 } }} />
                                            </IconButton>
                                            <IconButton 
                                                size="small"
                                                onClick={() => {
                                                    setData(null);
                                                    setEditUserClicked(null);
                                                }}
                                                sx={getActionButtonSx('error')}
                                            >
                                                <Cancel sx={{ fontSize: { xs: 14, md: 16 } }} />
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                
                {/* Поле пароля при добавлении/редактировании */}
                {(addUserClicked || editUserClicked) && (
                    <Box sx={{ 
                        mt: { xs: 1, md: 1.5 }, 
                        p: { xs: 1, md: 1.5 }, 
                        backgroundColor: addUserClicked ? 'var(--success-bg)' : 'var(--warning-bg)',
                        borderRadius: { xs: '6px', md: '8px' },
                        border: '1px solid var(--border-default)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: { xs: 1, md: 2 },
                        flexDirection: { xs: 'column', md: 'row' }
                    }}>
                        <Typography sx={{ 
                            fontSize: { xs: '10px', md: '11px' }, 
                            fontWeight: 600, 
                            color: 'var(--text-secondary)',
                            minWidth: { xs: 'auto', md: 60 },
                            width: { xs: '100%', md: 'auto' }
                        }}>
                            Пароль:
                        </Typography>
                        <TextField 
                            size="small" 
                            type="password"
                            placeholder={editUserClicked ? "Оставьте пустым, если не менять" : "Введите пароль"} 
                            fullWidth
                            sx={{
                                ...inputSx,
                                maxWidth: { xs: '100%', md: 300 }
                            }}
                            onChange={(e) => setData({ ...data, password: e.target.value })} 
                        />
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ 
                p: { xs: '8px 12px', md: '10px 16px' }, 
                backgroundColor: 'var(--background-default)',
                borderTop: '1px solid var(--border-default)',
                justifyContent: 'space-between',
                flexDirection: { xs: 'column-reverse', md: 'row' },
                gap: { xs: 0.75, md: 0 }
            }}>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PersonAdd sx={{ fontSize: { xs: 12, md: 14 } }} />}
                    onClick={() => {
                        setData({ roles: ['user'] });
                        setAddUserClicked({});
                        setEditUserClicked(false);
                    }}
                    disabled={addUserClicked || editUserClicked}
                    fullWidth={false}
                    sx={{
                        borderColor: successColor,
                        color: successColor,
                        borderRadius: { xs: '4px', md: '6px' },
                        textTransform: 'none',
                        fontSize: { xs: '10px', md: '11px' },
                        padding: { xs: '3px 10px', md: '4px 12px' },
                        width: { xs: '100%', md: 'auto' },
                        '&:hover': {
                            borderColor: successColor,
                            backgroundColor: alpha(successColor, 0.08)
                        },
                        '&.Mui-disabled': {
                            borderColor: 'var(--border-default)',
                            color: 'var(--text-muted)'
                        }
                    }}
                >
                    Добавить пользователя
                </Button>
                
                <Button 
                    size="small" 
                    variant="contained"
                    onClick={() => map.fire('modalclose')}
                    fullWidth={false}
                    sx={{
                        backgroundColor: primaryColor,
                        borderRadius: { xs: '4px', md: '6px' },
                        textTransform: 'none',
                        fontSize: { xs: '10px', md: '11px' },
                        padding: { xs: '3px 10px', md: '4px 12px' },
                        width: { xs: '100%', md: 'auto' },
                        '&:hover': { backgroundColor: alpha(primaryColor, 0.9) }
                    }}
                >
                    Закрыть
                </Button>
            </DialogActions>
        </Dialog>,
        document.body
    );
};

export default ModalUsers;
